<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <Header />

    <!-- Main Content -->
    <main class="flex-1">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid grid-cols-1 xl:grid-cols-8 gap-8">
          <!-- Left Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-20">
              <div
                class="bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-dashed border-blue-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-blue-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-blue-500 text-xs">120x600</div>
              </div>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="xl:col-span-6">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <!-- Left Column - Main Content -->
              <div class="lg:col-span-2 space-y-8">
                <FeaturedArticle />
                <LatestStories :articles="articles" :maxArticlesPerCategory="6" />
              </div>

              <!-- Right Column - Sidebar -->
              <div class="lg:col-span-1 space-y-6">
                <NewsStream :articles="articles" />
                <div class="lg:sticky lg:top-20 lg:self-start">
                  <ProductReviews :articles="articles" />
                </div>

                <!-- Disable until newsletter backend is ready -->

                <!-- <div class="lg:sticky lg:top-20 lg:self-start"> -->
                <!-- <NewsletterSignup /> -->
                <!-- </div> -->
              </div>
            </div>
          </div>

          <!-- Right Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-20">
              <div
                class="bg-gradient-to-br from-green-100 to-green-200 border-2 border-dashed border-green-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-green-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-green-500 text-xs">120x600</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <Footer />
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useHead } from '@vueuse/head';
  import Header from '../components/Header/Header.vue';
  import FeaturedArticle from '../components/FeaturedArticle/FeaturedArticle.vue';
  import LatestStories from '../components/LatestStories/LatestStories.vue';
  import NewsStream from '../components/NewsStream/NewsStream.vue';
  import ProductReviews from '../components/ProductReviews/ProductReviews.vue';
  import Footer from '../components/Footer/Footer.vue';
  import NewsletterSignup from '@/components/Common/NewsletterSignup.vue';

  // Import data processing utilities to load JSON data
  import { getAllArticles } from '@/utils/dataProcessor.js';

  // Setup SEO meta tags for homepage
  useHead({
    title: 'Hardware Pulse - Tech Reviews, News & Gaming Hardware',
    meta: [
      {
        name: 'description',
        content:
          'Stay updated with the latest tech reviews, gaming hardware benchmarks, and industry news at Hardware Pulse. Expert analysis and unbiased reviews.',
      },
      { property: 'og:title', content: 'Hardware Pulse - Tech Reviews, News & Gaming Hardware' },
      { property: 'og:description', content: 'Stay updated with the latest tech reviews, gaming hardware benchmarks, and industry news at Hardware Pulse.' },
      { property: 'og:url', content: 'https://hardwarepulse.com' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Hardware Pulse' },
      { property: 'og:image', content: 'https://hardwarepulse.com/src/assets/images/logo-static.svg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@hardwarepulse' },
      { name: 'keywords', content: 'hardware reviews, tech news, gaming hardware, computer hardware, GPU reviews, CPU benchmarks' },
    ],
    link: [{ rel: 'canonical', href: 'https://hardwarepulse.com' }],
  });

  // Reactive variable holding the articles loaded from JSON
  const articles = ref(getAllArticles());

  // Navigation items (if needed by header components)
  const primaryNavItems = [
    { label: 'Home', link: '/' },
    { label: 'News', link: '/news' },
    { label: 'Reviews', link: '/reviews' },
    { label: 'How To', link: '/how-to' },
    { label: 'Forums', link: '/forums' },
    { label: 'Contact', link: '/contact' },
  ];

  const trendingNavItems = [
    { label: 'Tech Innovation', link: '/trending/tech' },
    { label: 'Climate Change', link: '/trending/climate' },
    { label: 'Health Tips', link: '/trending/health' },
  ];
</script>

<style scoped>
  /* Layout styling is now handled by Tailwind utility classes */
</style>
